import { FormControl, FormHelperText } from '@mui/material';
import type { Control } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import { type Users_Bool_Exp } from '@graphql/__generated__/graphql';
import { RaLabel } from '@modules/_shared/components/form/RaLabel';
import {
  UserSelect,
  type UserSelectProps,
} from '@modules/_shared/components/user-select/UserSelect';
import { useTeamBrokerOptions } from '@modules/_shared/hooks/useTeamBrokerOptions';
import { useLocale } from '@shared/hooks/locale';

export type RaUserProps = {
  control: Control<any>;
  name: string;
  label: string;
  disabled?: boolean;
  required?: boolean;
  filters?: Users_Bool_Exp;
  multiple?: boolean;
  createUserSelected?: UserSelectProps['onCreate'];
  onSelectedUsersChange?: UserSelectProps['onSelectedUsersChange'];
  autoFocus?: boolean;
  /** Lists this team's active brokers on click; typing still runs the search. */
  shortlistTeamId?: string | null;
  openOnFocus?: boolean;
};

export const RaUser = ({
  control,
  name,
  label,
  required,
  createUserSelected,
  onSelectedUsersChange,
  filters,
  disabled = false,
  multiple = false,
  autoFocus = false,
  shortlistTeamId,
  openOnFocus,
}: RaUserProps) => {
  const { t } = useLocale();
  const shortlist = useTeamBrokerOptions(shortlistTeamId);
  const rules = {
    required: required ? t('This field is required') : undefined,
  };
  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <FormControl fullWidth error={!!error}>
          <RaLabel label={label} required={required} />
          <UserSelect
            userId={value}
            onChange={onChange}
            disabled={disabled}
            creatable={createUserSelected != null}
            onCreate={createUserSelected}
            onSelectedUsersChange={onSelectedUsersChange}
            filters={filters}
            multiple={multiple}
            autoFocus={autoFocus}
            defaultOptions={shortlist}
            openOnFocus={
              openOnFocus ?? (shortlistTeamId != null && shortlistTeamId !== '')
            }
          />
          {error?.message && <FormHelperText>{error.message}</FormHelperText>}
        </FormControl>
      )}
    />
  );
};
